import React, { useRef, useEffect, forwardRef } from 'react';
import { motion } from 'framer-motion';

const VariableProximity = forwardRef(({
  label,
  fromFontVariationSettings,
  toFontVariationSettings,
  containerRef,
  radius = 100,
  falloff = 'linear',
  className = '',
  style = {},
  onClick,
  ...restProps
}, ref) => {
  const letterRefs = useRef([]);
  const interpolatedSettingsRef = useRef([]);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const positionRef = useRef(null);

  const updatePosition = () => {
    // Initialize mouse position far away to avoid hover effect on load
    if (positionRef.current && mousePositionRef.current.x === 0 && mousePositionRef.current.y === 0) {
      mousePositionRef.current = {
        x: -9999,
        y: -9999
      };
    }
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    mousePositionRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const useMousePositionRef = () => {
    const lastPositionRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
      const updateMousePosition = (ev) => {
        lastPositionRef.current = { x: ev.clientX, y: ev.clientY };
        mousePositionRef.current = lastPositionRef.current;
      };

      window.addEventListener('mousemove', updateMousePosition);
      return () => window.removeEventListener('mousemove', updateMousePosition);
    }, []);

    return lastPositionRef;
  };

  useMousePositionRef();

  const parsedSettings = (() => {
    const parseSettings = (settingsStr) => {
      const regex = /'([^']+)'\s+([0-9.]+)/g;
      const settings = [];
      let match;
      while ((match = regex.exec(settingsStr)) !== null) {
        settings.push({ axis: match[1], value: parseFloat(match[2]) });
      }
      return settings;
    };

    const fromSettings = parseSettings(fromFontVariationSettings);
    const toSettings = parseSettings(toFontVariationSettings);

    return fromSettings.map((fromSetting) => {
      const toSetting = toSettings.find(s => s.axis === fromSetting.axis);
      return {
        axis: fromSetting.axis,
        fromValue: fromSetting.value,
        toValue: toSetting ? toSetting.value : fromSetting.value
      };
    });
  })();

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  const calculateDistance = (x1, y1, x2, y2) => {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  };

  const calculateFalloff = (distance) => {
    const norm = Math.max(0, Math.min(1, 1 - distance / radius));
    return falloff === 'linear' ? norm : norm * norm;
  };

  useEffect(() => {
    let frameId;
    
    const loop = () => {
      letterRefs.current.forEach((letterRef, index) => {
        if (!letterRef) return;

        const letterRect = letterRef.getBoundingClientRect();
        const letterCenterX = letterRect.left + letterRect.width / 2;
        const letterCenterY = letterRect.top + letterRect.height / 2;

        const distance = calculateDistance(
          mousePositionRef.current.x,
          mousePositionRef.current.y,
          letterCenterX,
          letterCenterY
        );

        if (distance >= radius) {
          letterRef.style.fontVariationSettings = fromFontVariationSettings;
          return;
        }

        const falloffValue = calculateFalloff(distance);
        const newSettings = parsedSettings
          .map(({ axis, fromValue, toValue }) => {
            const interpolatedValue = fromValue + (toValue - fromValue) * falloffValue;
            return `'${axis}' ${interpolatedValue}`;
          })
          .join(', ');

        interpolatedSettingsRef.current[index] = newSettings;
        letterRef.style.fontVariationSettings = newSettings;
      });

      frameId = requestAnimationFrame(loop);
    };

    loop();

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [fromFontVariationSettings, parsedSettings, radius]);

  const words = label.split(' ');
  let letterIndex = 0;

  return (
    <span
      ref={(el) => { 
        positionRef.current = el;
        if (ref) ref.current = el;
      }}
      className={`${className} variable-proximity`}
      onClick={onClick}
      style={{ display: 'inline', ...style }}
      {...restProps}
    >
      {words.map((word, wordIndex) => (
        <span
          key={wordIndex}
          style={{ display: 'inline-block', whiteSpace: 'nowrap' }}
        >
          {word.split('').map((letter) => {
            const currentLetterIndex = letterIndex++;
            return (
              <motion.span
                key={currentLetterIndex}
                ref={(el) => { letterRefs.current[currentLetterIndex] = el; }}
                style={{
                  display: 'inline-block',
                  fontVariationSettings: interpolatedSettingsRef.current[currentLetterIndex],
                }}
                aria-hidden="true"
              >
                {letter}
              </motion.span>
            );
          })}
          {wordIndex < words.length - 1 && (
            <span style={{ display: 'inline-block' }}>&nbsp;</span>
          )}
        </span>
      ))}
    </span>
  );
});

VariableProximity.displayName = 'VariableProximity';

export default VariableProximity; 